import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Sparkles, Pencil, Trash2, ImageIcon, Upload, X } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import { supabase } from '@/lib/supabase';
import {
  extractFromImage,
  validateImageFile,
  fileToBase64,
  joinExtractedStatements,
  extractErrorMessage,
  ExtractError,
  type AcceptedMediaType,
} from '@/lib/exercises/extract-from-image';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SchoolLevel } from '@jeraisonne/db';
import { usePublishedChapters, useChapterLessons } from '@/lib/admin/exercise-queries';
import { useCreateV2Exercise, useExerciseV2, exerciseV2QueryKey } from '@/lib/exercise-v2/api';
import { useDeleteExercise } from '@/lib/admin/exercise-mutations';
import {
  useCoherencePhase,
  useSolutionPhase,
  useClueHuntPhase,
  useDictationPhase,
  useAuditPhase,
  useDistractorsPhase,
  useImagePhase,
} from '@/lib/exercise-v2/use-v2-generation';
import { V2GenerationError, attachVisualsToStatement } from '@/lib/exercise-v2/generation-client';
import { PhaseCard } from './PhaseCard';
import { DictationCorrectionControl } from './DictationCorrectionControl';
import { SolutionSourceToggle, type SolutionSource } from './SolutionSourceToggle';
import {
  initialStatuses,
  setStatus,
  canRunPhase,
  canRunAll,
  AUTORUN_SEQUENCE,
  type PhaseId,
  type PhaseStatuses,
} from './phase-status';

const COLLEGE_LEVELS = SCHOOL_LEVELS.filter((l) => l.cycle === 'collège');
const LYCEE_LEVELS = SCHOOL_LEVELS.filter((l) => l.cycle === 'lycée');

function errMessage(err: unknown): string {
  if (err instanceof V2GenerationError) return err.message;
  return err instanceof Error ? err.message : String(err);
}

export function ExerciseV2Wizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: chapters } = usePublishedChapters();

  // ── Setup ─────────────────────────────────────────────────────────────────
  const [selectedLevel, setSelectedLevel] = useState<SchoolLevel | ''>('');
  const [chapterId, setChapterId] = useState('');
  const [lessonId, setLessonId] = useState('');
  const { data: lessons } = useChapterLessons(chapterId);
  const [rawStatement, setRawStatement] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');

  // ── Import depuis des photos (OCR vision + visuels structurés) ───────────────
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [imageFileError, setImageFileError] = useState<string | null>(null);
  // Visuels structurés (tableaux/figures/graphes) extraits des images, rattachés
  // au bloc `statement` une fois la phase cohérence passée.
  const [extractedVisuals, setExtractedVisuals] = useState<ExerciseVisualBlock[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Révoque les object URLs des vignettes au démontage.
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
    // Cleanup au démontage uniquement (révoque les object URLs).
  }, []);

  function handleAddImages(e: React.ChangeEvent<HTMLInputElement>) {
    setImageFileError(null);
    setExtractError(null);
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (picked.length === 0) return;

    const valid: File[] = [];
    for (const file of picked) {
      const err = validateImageFile(file);
      if (err) {
        setImageFileError(err);
        continue;
      }
      valid.push(file);
    }
    if (valid.length === 0) return;
    setImages((prev) => [...prev, ...valid]);
    setImagePreviews((prev) => [...prev, ...valid.map((f) => URL.createObjectURL(f))]);
  }

  function handleRemoveImage(index: number) {
    setImagePreviews((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
    setImages((prev) => prev.filter((_, i) => i !== index));
  }

  function clearImages() {
    setImagePreviews((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    setImages([]);
  }

  async function handleExtract() {
    if (images.length === 0) return;
    setExtracting(true);
    setExtractError(null);
    try {
      // Pour chaque image : OCR du texte + extraction des visuels structurés, en
      // parallèle. L'ordre du tableau (= ordre de sélection) est préservé, succès
      // partiel toléré. Les visuels sont facultatifs (l'image peut n'avoir aucune
      // figure).
      const results = await Promise.allSettled(
        images.map(async (file) => {
          const base64 = await fileToBase64(file);
          const media_type = file.type as AcceptedMediaType;
          const [textRes, visualRes] = await Promise.allSettled([
            extractFromImage({ image_base64: base64, media_type }),
            supabase.functions.invoke<{ visual_blocks?: ExerciseVisualBlock[] }>(
              'extract-visual-blocks',
              { body: { image_base64: base64, media_type } },
            ),
          ]);
          if (textRes.status === 'rejected') throw textRes.reason;
          const visuals =
            visualRes.status === 'fulfilled' && Array.isArray(visualRes.value.data?.visual_blocks)
              ? (visualRes.value.data!.visual_blocks as ExerciseVisualBlock[])
              : [];
          return { text: textRes.value.extracted_text, visuals };
        }),
      );

      const texts: string[] = [];
      const visuals: ExerciseVisualBlock[] = [];
      let firstError: string | null = null;
      for (const r of results) {
        if (r.status === 'fulfilled') {
          texts.push(r.value.text);
          visuals.push(...r.value.visuals);
        } else if (!firstError) {
          firstError =
            r.reason instanceof ExtractError
              ? extractErrorMessage(r.reason.code)
              : 'Erreur inattendue lors de l’extraction.';
        }
      }

      const extracted = joinExtractedStatements(texts);
      if (extracted || visuals.length > 0) {
        // Append à l'énoncé courant (texte collé + OCR cumulables).
        if (extracted) setRawStatement((prev) => joinExtractedStatements([prev, extracted]));
        if (visuals.length > 0) setExtractedVisuals((prev) => [...prev, ...visuals]);
        clearImages();
      }
      if (firstError) setExtractError(firstError);
    } catch {
      setExtractError('Erreur inattendue lors de l’extraction.');
    } finally {
      setExtracting(false);
    }
  }

  const filteredChapters = useMemo(
    () => (chapters ?? []).filter((c) => !selectedLevel || c.level === selectedLevel),
    [chapters, selectedLevel],
  );
  const chapter = useMemo(
    () => (chapters ?? []).find((c) => c.id === chapterId) ?? null,
    [chapters, chapterId],
  );

  // ── Génération ──────────────────────────────────────────────────────────────
  const [exerciseId, setExerciseId] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<PhaseStatuses>(initialStatuses);
  const statusesRef = useRef(statuses);
  statusesRef.current = statuses;
  const [errors, setErrors] = useState<Partial<Record<PhaseId, string | null>>>({});
  const [coherenceIssues, setCoherenceIssues] = useState<string[]>([]);
  const [auditNote, setAuditNote] = useState<string | null>(null);

  const [solutionSource, setSolutionSource] = useState<SolutionSource>('generate');

  const createV2 = useCreateV2Exercise();
  const coherence = useCoherencePhase(exerciseId ?? '');
  const solution = useSolutionPhase(exerciseId ?? '');
  const dictation = useDictationPhase(exerciseId ?? '');
  const audit = useAuditPhase(exerciseId ?? '');
  const distractors = useDistractorsPhase(exerciseId ?? '');
  const clueHunt = useClueHuntPhase(exerciseId ?? '');
  const image = useImagePhase(exerciseId ?? '');
  const deleteExercise = useDeleteExercise();
  const { data: v2 } = useExerciseV2(exerciseId);

  const counts = useMemo(() => {
    const blocks = v2?.blocks ?? [];
    return {
      questions: blocks.filter((b) => b.block_type === 'statement_question').length,
      corrections: blocks.filter((b) => b.block_type === 'correction').length,
      distractors: blocks.filter((b) => b.block_type === 'distractor').length,
      clueHunts: blocks.filter((b) => b.block_type === 'clue_hunt').length,
    };
  }, [v2]);
  const illustrationUrl = v2?.meta.illustration_url ?? null;

  const canCreate = !!chapterId && rawStatement.trim().length >= 20 && !createV2.isPending;

  async function handleCreate() {
    if (!canCreate) return;
    try {
      const id = await createV2.mutateAsync({
        chapter_id: chapterId,
        lesson_id: lessonId || null,
        raw_statement: rawStatement,
      });
      setExerciseId(id);
    } catch {
      /* l'erreur s'affiche via createV2.error */
    }
  }

  /** Lance une phase en pilotant son statut + la remontée d'erreur. */
  async function runPhase(id: PhaseId, fn: () => Promise<void>): Promise<boolean> {
    setStatuses((s) => setStatus(s, id, 'running'));
    setErrors((e) => ({ ...e, [id]: null }));
    try {
      await fn();
      setStatuses((s) => setStatus(s, id, 'done'));
      return true;
    } catch (err) {
      setStatuses((s) => setStatus(s, id, 'error'));
      setErrors((e) => ({ ...e, [id]: errMessage(err) }));
      return false;
    }
  }

  const runners: Record<PhaseId, () => Promise<boolean>> = {
    coherence: () =>
      runPhase('coherence', async () => {
        const res = await coherence.mutateAsync({ rawStatement, customInstructions });
        setCoherenceIssues(res.coherence.ok ? [] : res.coherence.issues);
        // Rattache les visuels extraits des images au bloc `statement` qui vient
        // d'être créé (le tuteur peut les déplacer par question dans l'éditeur).
        if (extractedVisuals.length > 0 && exerciseId) {
          await attachVisualsToStatement(exerciseId, extractedVisuals);
          await queryClient.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) });
        }
      }),
    solution: () =>
      runPhase('solution', async () => void (await solution.mutateAsync({ customInstructions }))),
    audit: () =>
      runPhase('audit', async () => {
        const res = await audit.mutateAsync();
        setAuditNote(
          res.audit_ok
            ? 'Corrigé validé, aucune rectification.'
            : `${res.fixed_count} correction(s) rectifiée(s).`,
        );
      }),
    distractors: () =>
      runPhase('distractors', async () => void (await distractors.mutateAsync({}))),
    clue_hunt: () => runPhase('clue_hunt', async () => void (await clueHunt.mutateAsync({}))),
    image: () =>
      runPhase('image', async () => {
        if (!chapter) throw new V2GenerationError('Chapitre introuvable');
        await image.mutateAsync({
          subject: chapter.subject,
          level: chapter.level,
          title: v2?.meta.title ?? 'Exercice',
        });
      }),
  };

  async function handleRunAll() {
    for (const id of AUTORUN_SEQUENCE) {
      if (statusesRef.current[id] === 'done') continue;
      const ok = await runners[id]();
      if (!ok) break;
    }
  }

  /** Structure un corrigé dicté (source `dictation`) ; pilote le statut « solution ». */
  async function runDictation(transcript: string) {
    await runPhase('solution', async () => {
      await dictation.mutateAsync({ transcript, customInstructions });
    });
  }

  async function handleDelete() {
    if (!exerciseId) return;
    if (!window.confirm('Supprimer ce brouillon et tout son contenu ?')) return;
    await deleteExercise.mutateAsync(exerciseId);
    navigate({ to: '/tutor/exercises' });
  }

  // ── Écran 1 : setup ───────────────────────────────────────────────────────
  if (!exerciseId) {
    const levelCls = (active: boolean) =>
      cn(
        'min-h-11 rounded-xl border px-3 font-sans text-[13px] font-semibold transition-colors active:scale-[0.98]',
        active
          ? 'border-primary-500 bg-primary-50 text-primary-700'
          : 'border-ink-200 text-ink-600',
      );
    const fieldCls =
      'border-ink-200 min-h-11 w-full rounded-xl border bg-white px-3 font-sans text-sm';

    return (
      <div className="pb-safe mx-auto max-w-3xl p-4 py-6 sm:p-6 sm:py-8">
        <Link
          to="/tutor/exercises"
          className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px]"
        >
          <ArrowLeft className="h-4 w-4" />
          Mes exercices
        </Link>
        <h1 className="font-display text-ink-900 mb-1 text-[22px] font-extrabold">
          Nouvel exercice guidé V2
        </h1>
        <p className="text-ink-500 mb-6 font-sans text-sm">
          Colle l'énoncé ou importe-le depuis des photos : on l'audite, on le découpe en questions
          courtes, puis on génère corrigé, distracteurs et illustration — phase par phase.
        </p>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="font-display text-ink-700 text-[12px] font-bold">Niveau</label>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
              {[...COLLEGE_LEVELS, ...LYCEE_LEVELS].map((l) => (
                <button
                  key={l.value}
                  type="button"
                  onClick={() => {
                    setSelectedLevel(selectedLevel === l.value ? '' : (l.value as SchoolLevel));
                    setChapterId('');
                    setLessonId('');
                  }}
                  className={levelCls(selectedLevel === l.value)}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-display text-ink-700 text-[12px] font-bold">Chapitre</label>
            <select
              value={chapterId}
              onChange={(e) => {
                setChapterId(e.target.value);
                setLessonId('');
              }}
              className={fieldCls}
            >
              <option value="">Choisir un chapitre…</option>
              {filteredChapters.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {chapterId && (lessons?.length ?? 0) > 0 && (
            <div className="flex flex-col gap-2">
              <label className="font-display text-ink-700 text-[12px] font-bold">
                Leçon (optionnel)
              </label>
              <select
                value={lessonId}
                onChange={(e) => setLessonId(e.target.value)}
                className={fieldCls}
              >
                <option value="">Aucune</option>
                {(lessons ?? []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Import depuis des photos (OCR) */}
          <div className="border-ink-200 flex flex-col gap-3 rounded-xl border border-dashed bg-white p-3">
            <div className="flex flex-col gap-0.5">
              <label className="font-display text-ink-700 text-[12px] font-bold">
                Importer depuis des photos
              </label>
              <p className="text-ink-400 font-sans text-[11px]">
                Une ou plusieurs images (JPEG/PNG/WebP, max 5 Mo) — le texte est ajouté à l'énoncé.
              </p>
            </div>

            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {imagePreviews.map((url, i) => (
                  <div
                    key={url}
                    className="border-ink-200 relative aspect-square overflow-hidden rounded-lg border"
                  >
                    <img src={url} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(i)}
                      aria-label={`Retirer la photo ${i + 1}`}
                      className="bg-ink-900/70 absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full text-white active:scale-95"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={handleAddImages}
            />

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={() => imageInputRef.current?.click()}
                disabled={extracting}
                className="min-h-11 w-full active:scale-[0.98]"
              >
                <Upload className="h-4 w-4" />
                Ajouter des photos
              </Button>
              <Button
                type="button"
                onClick={() => void handleExtract()}
                disabled={images.length === 0 || extracting}
                loading={extracting}
                className="min-h-11 w-full active:scale-[0.98]"
              >
                <Sparkles className="h-4 w-4" />
                Extraire le texte{images.length > 0 ? ` (${images.length})` : ''}
              </Button>
            </div>

            {imageFileError && (
              <p className="font-sans text-[12px] text-red-600">{imageFileError}</p>
            )}
            {extractError && <p className="font-sans text-[12px] text-red-600">{extractError}</p>}
            {extractedVisuals.length > 0 && (
              <p className="text-level-600 font-sans text-[11px]">
                {extractedVisuals.length} visuel(s) détecté(s) (tableau/figure) — rattaché(s) à
                l'énoncé après le découpage, repositionnables dans l'éditeur.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-display text-ink-700 text-[12px] font-bold">Énoncé brut</label>
            <textarea
              value={rawStatement}
              onChange={(e) => setRawStatement(e.target.value)}
              rows={7}
              placeholder="Colle ici l'énoncé de l'exercice…"
              className="border-ink-200 w-full rounded-xl border bg-white p-3 font-sans text-sm"
            />
            <p className="text-ink-400 font-sans text-[11px]">
              {rawStatement.trim().length} caractères (min. 20)
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-display text-ink-700 text-[12px] font-bold">
              Consignes (optionnel)
            </label>
            <textarea
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              rows={2}
              placeholder="Ex. : reste sur des nombres entiers, contexte sportif…"
              className="border-ink-200 w-full rounded-xl border bg-white p-3 font-sans text-sm"
            />
          </div>

          {createV2.error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 font-sans text-[12px] text-red-600">
              {errMessage(createV2.error)}
            </p>
          )}

          <Button
            type="button"
            onClick={handleCreate}
            disabled={!canCreate}
            loading={createV2.isPending}
            className="min-h-11 w-full active:scale-[0.98]"
          >
            <Sparkles className="h-4 w-4" />
            Créer le brouillon et démarrer
          </Button>
        </div>
      </div>
    );
  }

  // ── Écran 2 : phases ──────────────────────────────────────────────────────
  return (
    <div className="pb-safe mx-auto max-w-3xl p-4 py-6 sm:p-6 sm:py-8">
      <Link
        to="/tutor/exercises"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px]"
      >
        <ArrowLeft className="h-4 w-4" />
        Mes exercices
      </Link>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-ink-900 text-[22px] font-extrabold">Génération V2</h1>
      </div>

      <Button
        type="button"
        onClick={handleRunAll}
        disabled={!canRunAll(statuses)}
        className="mb-5 min-h-11 w-full active:scale-[0.98]"
      >
        <Sparkles className="h-4 w-4" />
        Tout enchaîner
      </Button>

      <div className="flex flex-col gap-3">
        <PhaseCard
          index={1}
          title="Cohérence & découpage"
          description="Audite l'énoncé et le découpe en questions courtes."
          status={statuses.coherence}
          canRun={canRunPhase('coherence', statuses)}
          onRun={() => void runners.coherence()}
          error={errors.coherence}
        >
          {counts.questions > 0 && (
            <p className="text-ink-600 font-sans text-[12px]">
              {counts.questions} question(s) générée(s).
            </p>
          )}
          {coherenceIssues.length > 0 && (
            <ul className="mt-1 list-disc space-y-0.5 rounded-lg bg-amber-50 px-4 py-2 font-sans text-[12px] text-amber-700">
              {coherenceIssues.map((iss, i) => (
                <li key={i}>{iss}</li>
              ))}
            </ul>
          )}
        </PhaseCard>

        <PhaseCard
          index={2}
          title="Corrigé pédagogique"
          description={
            solutionSource === 'dictation'
              ? 'Dicte ta correction à voix haute ; l’IA la structure en étapes.'
              : 'Rédige le corrigé en étapes-clés courtes pour chaque question.'
          }
          status={statuses.solution}
          canRun={solutionSource === 'generate' && canRunPhase('solution', statuses)}
          onRun={() => void runners.solution()}
          error={solutionSource === 'generate' ? errors.solution : null}
          hideRunButton={solutionSource === 'dictation'}
        >
          <SolutionSourceToggle
            value={solutionSource}
            onChange={setSolutionSource}
            disabled={statuses.solution === 'running'}
          />
          {solutionSource === 'dictation' &&
            (canRunPhase('solution', statuses) ||
              statuses.solution === 'running' ||
              statuses.solution === 'done') && (
              <DictationCorrectionControl
                onStructure={runDictation}
                structuring={statuses.solution === 'running'}
                structureError={errors.solution}
              />
            )}
          {solutionSource === 'dictation' && statuses.coherence !== 'done' && (
            <p className="text-ink-500 mt-2 font-sans text-[12px]">
              Lance d’abord la cohérence pour découper les questions.
            </p>
          )}
          {counts.corrections > 0 && (
            <p className="text-ink-600 mt-2 font-sans text-[12px]">
              {counts.corrections} étape(s) de corrigé générée(s).
            </p>
          )}
        </PhaseCard>

        <PhaseCard
          index={3}
          title="Audit du corrigé"
          description="Vérifie chaque réponse et rectifie au besoin (économe en tokens)."
          status={statuses.audit}
          canRun={canRunPhase('audit', statuses)}
          onRun={() => void runners.audit()}
          error={errors.audit}
        >
          {auditNote && <p className="text-ink-600 font-sans text-[12px]">{auditNote}</p>}
        </PhaseCard>

        <PhaseCard
          index={4}
          title="Distracteurs"
          description="Génère des choix faux plausibles pour chaque question."
          status={statuses.distractors}
          canRun={canRunPhase('distractors', statuses)}
          onRun={() => void runners.distractors()}
          error={errors.distractors}
        >
          {counts.distractors > 0 && (
            <p className="text-ink-600 font-sans text-[12px]">
              {counts.distractors} distracteur(s) généré(s).
            </p>
          )}
        </PhaseCard>

        <PhaseCard
          index={5}
          title="Chasse aux indices"
          description="Extrait 2-4 indices cliquables de l'énoncé (optionnel — sauté si non pertinent)."
          status={statuses.clue_hunt}
          canRun={canRunPhase('clue_hunt', statuses)}
          onRun={() => void runners.clue_hunt()}
          error={errors.clue_hunt}
        >
          {counts.clueHunts > 0 && (
            <p className="text-ink-600 font-sans text-[12px]">
              {counts.clueHunts} chasse{counts.clueHunts > 1 ? 's' : ''} aux indices générée
              {counts.clueHunts > 1 ? 's' : ''}.
            </p>
          )}
          {clueHunt.data?.no_clues_emitted && counts.clueHunts === 0 && (
            <p className="text-ink-500 font-sans text-[12px]">
              Le LLM n'a pas trouvé d'indices pertinents à extraire.
            </p>
          )}
        </PhaseCard>

        <PhaseCard
          index={6}
          title="Illustration"
          description="Génère le prompt d'image adéquat puis l'illustration manga."
          status={statuses.image}
          canRun={canRunPhase('image', statuses)}
          onRun={() => void runners.image()}
          error={errors.image}
        >
          {illustrationUrl ? (
            <img
              src={illustrationUrl}
              alt="Illustration générée"
              className="mt-1 h-32 w-full rounded-xl object-cover"
            />
          ) : (
            <p className="text-ink-400 flex items-center gap-1.5 font-sans text-[12px]">
              <ImageIcon className="h-3.5 w-3.5" />
              Aucune illustration pour l'instant.
            </p>
          )}
        </PhaseCard>
      </div>

      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <Button asChild variant="outline" className="min-h-11 w-full active:scale-[0.98]">
          <Link to="/tutor/exercises/$exerciseId/edit-v2" params={{ exerciseId }}>
            <Pencil className="h-4 w-4" />
            Ouvrir dans l'éditeur V2
          </Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={handleDelete}
          loading={deleteExercise.isPending}
          className="text-error-500 min-h-11 w-full active:scale-[0.98]"
        >
          <Trash2 className="h-4 w-4" />
          Supprimer ce brouillon
        </Button>
      </div>
    </div>
  );
}
